import { safeUrl } from "../util.js"

export class PwaManifestContents extends HTMLElement {
  static tagName = "pwa-manifest-contents"

  #statusOutput
  #properties

  static #URL_KEYS = new Set(["start_url", "scope", "id", "src", "url", "action", "href"])

  static #COLOR_KEYS = new Set(["background_color", "theme_color"])

  connectedCallback() {
    this.#statusOutput = this.querySelector("output[name='status']")
    this.#properties = this.querySelector("pwa-manifest-properties")
  }

  showLoading(status = "loading") {
    for (const element of this.#properties.children) {
      if (!element.hasAttribute("apple")) element.hidden = true
    }
    this.#statusOutput.textContent = ""
    this.removeAttribute("nonstandard")
    this.setAttribute("status", status)
  }

  showError(message) {
    for (const element of this.#properties.children) {
      if (!element.hasAttribute("apple")) element.hidden = true
    }
    this.#statusOutput.textContent = message
    this.removeAttribute("nonstandard")
    this.setAttribute("status", "error")
  }

  show(data, baseUrl) {
    for (const element of this.#properties.children) {
      if (!element.hasAttribute("apple")) element.hidden = true
    }
    this.#statusOutput.textContent = ""
    document.querySelectorAll("pwa-manifest-nonstandard-property").forEach( (e) => e.remove() )

    for (const [key, value] of Object.entries(data)) {
      const tagName = "pwa-manifest-" + key.replaceAll("_", "-")
      const element = /^[a-z][a-z0-9-]*$/.test(tagName) ? this.#properties.querySelector(tagName) : null

      if (element) {
        this.#populateProperty(element, key, value, baseUrl)
        element.hidden = false
      } else {
        const properties = document.querySelector("pwa-manifest-properties")
        const propertyEl = document.createElement("pwa-manifest-nonstandard-property")
        propertyEl.setAttribute("property-name", key)
        const keyEl = document.createElement("pwa-manifest-property-key")
        keyEl.setAttribute("nonstandard", "")
        keyEl.textContent = key
        const badge = document.createElement("pwa-nonstandard-badge")
        badge.textContent = "non-standard"
        keyEl.appendChild(badge)
        const valueEl = document.createElement("pwa-manifest-property-value")
        valueEl.setAttribute("manifest-key",key)
        propertyEl.appendChild(keyEl)
        propertyEl.appendChild(valueEl)
        properties.appendChild(propertyEl)
        this.#populateProperty(propertyEl, key, value, baseUrl)
      }
    }

    this.setAttribute("status", "found")
  }

  hide() {
    this.removeAttribute("status")
    this.removeAttribute("nonstandard")
    this.#statusOutput.textContent = ""
    for (const element of this.#properties.children) {
      element.hidden = true
      const valueContainer = element.querySelector("pwa-manifest-property-value")
      if (valueContainer) valueContainer.innerHTML = ""
    }
  }

  showApple(data, baseUrl) {
    if (data.touchIcons.length > 0) {
      const list = document.createElement("ol")
      for (const { href, sizes } of data.touchIcons) {
        const li = document.createElement("li")
        if (href) {
          const resolved = safeUrl(new URL(href, baseUrl).href)
          if (resolved) {
            const link = document.createElement("a")
            link.href = resolved
            const img = document.createElement("img")
            img.src = resolved
            img.loading = "lazy"
            img.alt = sizes ? `${sizes} apple-touch-icon` : "apple-touch-icon"
            link.appendChild(img)
            li.appendChild(link)
          }
        }
        const dl = document.createElement("dl")
        if (href) {
          const dt = document.createElement("dt")
          dt.textContent = "href"
          const dd = document.createElement("dd")
          const code = document.createElement("code")
          code.textContent = href
          dd.appendChild(code)
          dl.appendChild(dt)
          dl.appendChild(dd)
        }
        if (sizes) {
          const dt = document.createElement("dt")
          dt.textContent = "sizes"
          const dd = document.createElement("dd")
          dd.textContent = sizes
          dl.appendChild(dt)
          dl.appendChild(dd)
        }
        li.appendChild(dl)
        list.appendChild(li)
      }
      const element = this.#properties.querySelector("pwa-apple-touch-icon")
      element.querySelector("pwa-manifest-property-value").appendChild(list)
      element.hidden = false
    }

    if (data.startupImages.length > 0) {
      const list = document.createElement("ol")
      for (const { href, media } of data.startupImages) {
        const li = document.createElement("li")
        const dl = document.createElement("dl")
        if (href) {
          const dt = document.createElement("dt")
          dt.textContent = "href"
          const dd = document.createElement("dd")
          const code = document.createElement("code")
          code.textContent = href
          dd.appendChild(code)
          dl.appendChild(dt)
          dl.appendChild(dd)
        }
        if (media) {
          const dt = document.createElement("dt")
          dt.textContent = "media"
          const dd = document.createElement("dd")
          const code = document.createElement("code")
          code.textContent = media
          dd.appendChild(code)
          dl.appendChild(dt)
          dl.appendChild(dd)
        }
        li.appendChild(dl)
        list.appendChild(li)
      }
      const element = this.#properties.querySelector("pwa-apple-touch-startup-image")
      element.querySelector("pwa-manifest-property-value").appendChild(list)
      element.hidden = false
    }

    const metaFields = [
      ["pwa-apple-mobile-web-app-capable",          data.webAppCapable],
      ["pwa-apple-mobile-web-app-title",            data.webAppTitle],
      ["pwa-apple-mobile-web-app-status-bar-style", data.statusBarStyle],
    ]
    for (const [tagName, value] of metaFields) {
      if (value) {
        const element = this.#properties.querySelector(tagName)
        element.querySelector("pwa-manifest-property-value").textContent = value
        element.hidden = false
      }
    }
  }

  #populateProperty(element, key, value, baseUrl) {
    const valueContainer = element.querySelector("pwa-manifest-property-value")
    valueContainer.innerHTML = ""

    if (value === null) {
      const nullEl = document.createElement("pwa-manifest-null-value")
      nullEl.textContent = "null"
      valueContainer.appendChild(nullEl)
      return
    }

    if (Array.isArray(value) || typeof value === "object") {
      valueContainer.appendChild(this.#renderValue(key, value, baseUrl))
      return
    }

    if (PwaManifestContents.#COLOR_KEYS.has(key)) {
      const colorValue = String(value)
      if (!CSS.supports("color", colorValue)) {
        valueContainer.textContent = colorValue
        return
      }

      const swatch = document.createElement("pwa-color-swatch")

      const color = document.createElement("span")
      color.style.setProperty("--swatch-color", colorValue)

      const hexCode = document.createElement("code")
      hexCode.textContent = colorValue

      swatch.appendChild(color)
      swatch.appendChild(hexCode)

      valueContainer.appendChild(swatch)
      return
    }

    if (PwaManifestContents.#URL_KEYS.has(key)) {
      const code = document.createElement("code")
      code.textContent = String(value)
      valueContainer.appendChild(code)
      return
    }

    valueContainer.textContent = String(value)
  }

  #renderValue(key, value, baseUrl) {
    if (value === null) {
      const el = document.createElement("pwa-manifest-null-value")
      el.textContent = "null"
      return el
    }

    if (Array.isArray(value)) {
      return this.#renderArray(key, value, baseUrl)
    }

    if (typeof value === "object") {
      return this.#renderObject(value, baseUrl)
    }

    if (PwaManifestContents.#URL_KEYS.has(key)) {
      const code = document.createElement("code")
      code.textContent = String(value)
      return code
    }

    if (PwaManifestContents.#COLOR_KEYS.has(key)) {
      const colorValue = String(value)
      if (!CSS.supports("color", colorValue)) {
        const span = document.createElement("span")
        span.textContent = colorValue
        return span
      }
      const wrapper = document.createElement("span")
      const swatch = document.createElement("pwa-color-swatch")
      swatch.style.setProperty("--swatch-color", colorValue)
      wrapper.appendChild(swatch)
      wrapper.appendChild(document.createTextNode(` ${colorValue}`))
      return wrapper
    }

    const span = document.createElement("span")
    span.textContent = String(value)
    return span
  }

  #renderArray(key, items, baseUrl) {
    const isIconList = (key === "icons" || key === "screenshots")
    const list = document.createElement("ol")

    for (const item of items) {
      const li = document.createElement("li")

      if (isIconList && typeof item === "object" && item !== null && item.src) {
        const resolved = safeUrl(new URL(item.src, baseUrl).href)
        if (resolved) {
          const img = document.createElement("img")
          img.src = resolved
          img.alt = item.sizes ? `${item.sizes} icon` : "icon"
          img.loading = "lazy"

          const imgLink = document.createElement("a")
          imgLink.href = resolved
          imgLink.appendChild(img)

          li.appendChild(imgLink)
        }
        li.appendChild(this.#renderObject(item, baseUrl))
      } else if (typeof item === "object" && item !== null) {
        li.appendChild(this.#renderObject(item, baseUrl))
      } else {
        li.textContent = String(item)
      }

      list.appendChild(li)
    }

    return list
  }

  #renderObject(obj, baseUrl) {
    const subList = document.createElement("dl")

    for (const [k, v] of Object.entries(obj)) {
      const dt = document.createElement("dt")
      dt.setAttribute("manifest-object-key",k)
      dt.textContent = k
      const dd = document.createElement("dd")
      dd.appendChild(this.#renderValue(k, v, baseUrl))
      dd.setAttribute("manifest-object-key",k)
      subList.appendChild(dt)
      subList.appendChild(dd)
    }

    return subList
  }
}
